// Keyboard navigation of the menus: arrow keys move the focus to the nearest
// focusable element in that direction (works for lists, grids, tab bars and
// forms alike); Tab keeps its normal order. Text fields and sliders keep the
// arrow keys they need.

import { useEffect } from 'preact/hooks';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])';

type Dir = 'up' | 'down' | 'left' | 'right';
const DIRS: Record<string, Dir> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };

/** Does the element use this arrow key itself? */
function ownsArrow(el: Element, dir: Dir): boolean {
  if (el instanceof HTMLTextAreaElement) return true;
  // a closed select changes its value with up / down
  if (el instanceof HTMLSelectElement) return dir === 'up' || dir === 'down';
  if (el instanceof HTMLInputElement) {
    const type = el.type;
    if (type === 'range') return dir === 'left' || dir === 'right';
    if (type === 'number') return dir === 'up' || dir === 'down';
    if (type === 'checkbox' || type === 'radio' || type === 'button' || type === 'submit') return false;
    // text: left / right move the caret
    return dir === 'left' || dir === 'right';
  }
  return false;
}

function visible(el: HTMLElement): boolean {
  if (el.closest('[inert]')) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** The focusable element nearest to `from` in direction `dir` (null if none). */
export function nextInDirection(from: HTMLElement, candidates: HTMLElement[], dir: Dir): HTMLElement | null {
  const a = from.getBoundingClientRect();
  const ax = a.left + a.width / 2;
  const ay = a.top + a.height / 2;
  let best: HTMLElement | null = null;
  let bestScore = Infinity;
  for (const el of candidates) {
    if (el === from) continue;
    const b = el.getBoundingClientRect();
    const bx = b.left + b.width / 2;
    const by = b.top + b.height / 2;
    let main: number;
    let cross: number;
    switch (dir) {
      case 'up':
        main = a.top - b.bottom;
        cross = bx - ax;
        if (by >= ay - 1) continue;
        break;
      case 'down':
        main = b.top - a.bottom;
        cross = bx - ax;
        if (by <= ay + 1) continue;
        break;
      case 'left':
        main = a.left - b.right;
        cross = by - ay;
        if (bx >= ax - 1) continue;
        break;
      case 'right':
        main = b.left - a.right;
        cross = by - ay;
        if (bx <= ax + 1) continue;
        break;
    }
    // prefer elements straight in that direction: the sideways offset counts more
    const score = Math.max(0, main) + Math.abs(cross) * 2.5;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  return best;
}

/**
 * Arrow key focus navigation inside the element returned by `getRoot`
 * (the topmost layer: an open modal or drawer takes the focus to itself).
 */
export function useFocusNav(getRoot: () => HTMLElement | null, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const dir = DIRS[e.key];
      if (!dir || e.altKey || e.ctrlKey || e.metaKey || e.defaultPrevented) return;
      const root = getRoot();
      if (!root) return;
      const active = document.activeElement as HTMLElement | null;
      const candidates = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(visible);
      if (!candidates.length) return;
      if (!active || active === document.body || !root.contains(active)) {
        e.preventDefault();
        candidates[0].focus();
        return;
      }
      if (ownsArrow(active, dir)) return;
      const next = nextInDirection(active, candidates, dir);
      if (next) {
        e.preventDefault();
        next.focus();
        next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, getRoot]);
}
